/** @jsx jsx */
import { memo, useCallback, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { PrismAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useGlobalTheme } from '@atlaskit/theme/components';
import { useHighlightLines } from './internal/hooks/use-highlight';
import { getCodeBlockStyles, getCodeBlockTheme } from './internal/theme/styles';
import { normalizeLanguage } from './internal/utils/get-normalized-language';
import { createBidiWarningRenderer } from './react-syntax-highlighter-bidi-warning-renderer';
/**
 * __Code block__
 *
 * A code block highlights an entire block of code and keeps the formatting.
 *
 * - [Examples](https://atlassian.design/components/code/code-block/examples)
 * - [Code](https://atlassian.design/components/code/code-block/code)
 * - [Usage](https://atlassian.design/components/code/code-block/usage)
 */

var CodeBlock = /*#__PURE__*/memo(function CodeBlock(_ref) {
  var _ref$showLineNumbers = _ref.showLineNumbers,
      showLineNumbers = _ref$showLineNumbers === void 0 ? true : _ref$showLineNumbers,
      _ref$language = _ref.language,
      providedLanguage = _ref$language === void 0 ? 'text' : _ref$language,
      _ref$highlight = _ref.highlight,
      highlight = _ref$highlight === void 0 ? '' : _ref$highlight,
      _ref$highlightedStart = _ref.highlightedStartText,
      highlightedStartText = _ref$highlightedStart === void 0 ? 'Highlight start' : _ref$highlightedStart,
      _ref$highlightedEndTe = _ref.highlightedEndText,
      highlightedEndText = _ref$highlightedEndTe === void 0 ? 'Highlight end' : _ref$highlightedEndTe,
      testId = _ref.testId,
      text = _ref.text,
      _ref$codeBidiWarnings = _ref.codeBidiWarnings,
      codeBidiWarnings = _ref$codeBidiWarnings === void 0 ? true : _ref$codeBidiWarnings,
      codeBidiWarningLabel = _ref.codeBidiWarningLabel,
      _ref$codeBidiWarningT = _ref.codeBidiWarningTooltipEnabled,
      codeBidiWarningTooltipEnabled = _ref$codeBidiWarningT === void 0 ? true : _ref$codeBidiWarningT;
  var numLines = (text || '').split('\n').length;
  var globalTheme = useGlobalTheme();
  var theme = useMemo(function () {
    return getCodeBlockTheme(globalTheme, numLines);
  }, [globalTheme, numLines]);
  var getStyles = useMemo(function () {
    return getCodeBlockStyles(theme);
  }, [theme]);
  var styles = useMemo(function () {
    return getStyles(highlightedStartText, highlightedEndText, showLineNumbers);
  }, [highlightedStartText, highlightedEndText, showLineNumbers, getStyles]);

  var _useHighlightLines = useHighlightLines({
    highlight: highlight,
    testId: testId
  }),
      getHighlightStyles = _useHighlightLines.getHighlightStyles,
      highlightedLines = _useHighlightLines.highlightedLines;

  var getLineProps = useCallback(function (line) {
    return getHighlightStyles(line, highlightedLines);
  }, [getHighlightStyles, highlightedLines]);
  var language = useMemo(function () {
    return normalizeLanguage(providedLanguage);
  }, [providedLanguage]); // https://product-fabric.atlassian.net/browse/DST-2472

  var languageToUse = text ? language : 'text';
  var renderer = codeBidiWarnings ? createBidiWarningRenderer({
    codeBidiWarningLabel: codeBidiWarningLabel,
    codeBidiWarningTooltipEnabled: codeBidiWarningTooltipEnabled
  }) : undefined;
  return jsx(SyntaxHighlighter, {
    "data-testid": testId,
    "data-code-lang": language,
    "data-ds--code--code-block": "",
    css: styles,
    language: languageToUse,
    PreTag: "span",
    showLineNumbers: showLineNumbers // Wrap lines is needed to set styles on the line when highlighting.
    ,
    wrapLines: highlight.length > 0 || !!testId,
    lineProps: getLineProps,
    useInlineStyles: false,
    renderer: renderer
  }, text);
});
CodeBlock.displayName = 'CodeBlock';
export default CodeBlock;